import React, { useState } from 'react';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import ApexCharts from 'react-apexcharts';
import { useTranslation } from 'react-i18next';

interface QuestionSummaryChartsProps {
  questions: Array<QuestionReportDto>;
}

type QuestionReportDto = {
  question: string;
  choiceStatistics: Array<{
    choice?: string;
    count?: number;
  }>;
};

export const QuestionSummaryCharts: React.FC<QuestionSummaryChartsProps> = ({
  questions,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | 'all'>('all');
  const { t } = useTranslation(['common']);

  const selectedQuestion =
    typeof selectedIndex === 'number' ? questions[selectedIndex] : null;

  // Calculate answer count for each question
  const getAnswerCount = (q: QuestionReportDto) =>
    q.choiceStatistics?.reduce((sum, c) => sum + (c.count ?? 0), 0) ?? 0;

  const getChartData = () => {
    if (typeof selectedIndex === 'number') {
      // Single question view
      const allChoices = selectedQuestion?.choiceStatistics
        ?.map((c) => c.choice)
        .filter(Boolean) as Array<string>;

      return {
        series: [
          {
            name: selectedQuestion?.question,
            data:
              allChoices?.map((choice) => {
                const stat = selectedQuestion?.choiceStatistics?.find(
                  (c) => c.choice === choice
                );
                return stat?.count ?? 0;
              }) ?? [],
          },
        ],
        categories: allChoices,
      };
    }

    // All answers view - aggregate by choices
    const choiceMap = new Map<string, number>();

    // Collect all unique choices and their total counts
    questions.forEach((question) => {
      question.choiceStatistics.forEach((stat) => {
        if (stat.choice) {
          const currentCount = choiceMap.get(stat.choice) ?? 0;
          choiceMap.set(stat.choice, currentCount + (stat.count ?? 0));
        }
      });
    });

    // Sort choices by count in descending order
    const sortedChoices = Array.from(choiceMap.entries()).sort(
      ([, countA], [, countB]) => countB - countA
    );

    return {
      series: [
        {
          name: 'Total Responses',
          data: sortedChoices.map(([, count]) => count),
        },
      ],
      categories: sortedChoices.map(([choice]) => choice),
    };
  };

  const chartData = getChartData();

  const options: ApexCharts['props']['options'] = {
    chart: {
      type: 'bar',
      stacked: false,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '80%',
        borderRadius: 0,
        distributed: selectedIndex === 'all',
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          fontSize: '14px',
          colors: '#222',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        const total =
          chartData.series[0]?.data?.reduce((a, b) => a + b, 0) ?? 0;
        const percent =
          total > 0 ? ` (${((val / total) * 100).toFixed(2)}%)` : '';
        return `${val}${percent}`;
      },
      style: {
        fontSize: '12px',
        colors: ['white'],
      },
      offsetX: 8,
    },
    colors: ['#B56BFF'],
    legend: {
      show: false,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} responses`,
      },
    },
    grid: {
      borderColor: '#F3F3F3',
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
  };

  return (
    <HStack
      borderWidth="1px"
      borderColor="gray.50"
      bg="white"
      borderRadius="xl"
      p={4}
      alignItems="flex-start"
      spacing={8}
      w="full"
      minH="420px"
    >
      <Box
        minW="320px"
        maxW="340px"
        borderWidth="1px"
        borderColor="gray.50"
        borderRadius="xl"
        px={2}
        pt={4}
        bg="gray.25"
        h="full"
      >
        <Text
          color="gray.500"
          fontWeight="bold"
          fontSize="sm"
          px={3}
          mb={2}
          letterSpacing={0.2}
        >
          {t('common:questions').toUpperCase()} ({questions.length})
        </Text>
        <HStack
          spacing={0}
          mb={3}
          px={3}
          py={2}
          border="1px solid"
          borderColor={selectedIndex === 'all' ? 'purple.400' : 'gray.100'}
          borderRadius="lg"
          bg={selectedIndex === 'all' ? 'primary.25' : 'white'}
          w="calc(100% - 16px)"
          mx={2}
          justifyContent="space-between"
          alignItems="center"
          cursor="pointer"
          onClick={() => setSelectedIndex('all')}
          _hover={{ bg: selectedIndex === 'all' ? 'primary.25' : 'gray.50' }}
        >
          <Text
            fontWeight="semibold"
            color={selectedIndex === 'all' ? 'purple.600' : 'gray.700'}
            fontSize="xs"
          >
            {t('common:totalAnswers').toUpperCase()}
          </Text>
          <Text
            fontWeight="semibold"
            color={selectedIndex === 'all' ? 'purple.600' : 'gray.700'}
            fontSize="xs"
          >
            {questions.reduce(
              (sum, q) =>
                sum +
                (q.choiceStatistics?.reduce((s, c) => s + (c.count ?? 0), 0) ??
                  0),
              0
            )}{' '}
            {t('common:answer2').toUpperCase()}
          </Text>
        </HStack>
        <Stack px={2} spacing={2} maxH="540px" overflowY="auto" pr={1}>
          {questions.map((q, idx) => {
            return (
              <Box
                key={q.question}
                borderWidth={1}
                borderColor={selectedIndex === idx ? 'primary.100' : 'gray.100'}
                bg={selectedIndex !== idx ? 'white' : 'primary.25'}
                borderRadius="lg"
                boxShadow={selectedIndex === idx ? 'sm' : 'none'}
                cursor="pointer"
                px={4}
                py={2}
                mb={1}
                transition="border 0.2s, box-shadow 0.2s, background 0.2s"
                onClick={() => setSelectedIndex(idx)}
                _hover={{ bg: selectedIndex === idx ? 'white' : 'gray.50' }}
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <HStack justifyContent="space-between" mb={1}>
                  <Text
                    fontSize="xs"
                    color={selectedIndex === idx ? 'primary.400' : 'gray.500'}
                    fontWeight="bold"
                  >
                    {t('common:detail.question')} {idx + 1}
                  </Text>
                  <Text
                    fontSize="xs"
                    color={selectedIndex === idx ? 'primary.400' : 'gray.500'}
                    fontWeight="bold"
                  >
                    {getAnswerCount(q)} {t('common:answer2').toUpperCase()}
                  </Text>
                </HStack>
                <Text
                  fontSize="sm"
                  color={selectedIndex === idx ? 'primary.500' : 'gray.800'}
                  fontWeight={selectedIndex === idx ? 'semibold' : 'normal'}
                  noOfLines={3}
                  wordBreak="break-word"
                >
                  {q.question}
                </Text>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box flex="1" minW="0" h="full" display="flex" flexDirection="column">
        {selectedIndex === 'all' ? (
          <>
            <Text fontWeight="semibold" mb={4} color="gray.800">
              {t('common:allAnswers').toUpperCase()}
            </Text>
            <Stack flex={1} h="100%" minH={0}>
              <Box flex={1} h="100%" minH={0}>
                <ApexCharts
                  options={options}
                  series={chartData.series}
                  type="bar"
                  height="100%"
                  width="100%"
                />
              </Box>
            </Stack>
          </>
        ) : selectedQuestion ? (
          <>
            <Text fontWeight="semibold" mb={4} color="gray.800">
              {selectedQuestion?.question}
            </Text>
            <Stack flex={1} h="100%" minH={0}>
              <Box flex={1} h="100%" minH={0}>
                <ApexCharts
                  options={options}
                  series={chartData.series}
                  type="bar"
                  height={chartData.categories.length < 5 ? 200 : '100%'}
                  width="100%"
                />
              </Box>
            </Stack>
          </>
        ) : (
          <Text color="gray.500">{t('common:noQuestionSelected')}</Text>
        )}
      </Box>
    </HStack>
  );
};
